// API van de Productie Breakdown (/Api/productie-breakdown). Verwijderen gebeurt in de
// schermen zelf (achter bevestig()), zodat het vangnet bij de DELETE-aanroep staat.
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

const BASIS = `${BACKEND_URL}/Api/productie-breakdown`;

export async function api(pad, { method = 'GET', body, form, wat = 'Productie Breakdown' } = {}) {
  const r = await authFetch(`${BASIS}${pad}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: form || (body !== undefined ? JSON.stringify(body) : undefined),
  });
  if (r.status === 204) {
    if (!r.ok) await leesJson(r, wat);
    return null;
  }
  return leesJson(r, wat);
}

// Mutaties per item na elkaar uitvoeren: een tweede blur/status/keuze wacht tot de vorige
// klaar is. Zo komen de antwoorden altijd in volgorde binnen en kan een ouder antwoord een
// nieuwer item op het scherm niet terugdraaien (ook op de server landen ze in volgorde).
const itemRijen = new Map();
export function perItem(itemId, taak) {
  const vorige = itemRijen.get(itemId) || Promise.resolve();
  const deze = vorige.catch(() => {}).then(taak);
  const staart = deze.catch(() => {});
  itemRijen.set(itemId, staart);
  staart.then(() => { if (itemRijen.get(itemId) === staart) itemRijen.delete(itemId); });
  return deze;
}

// Alleen http(s)-links klikbaar maken (de server weigert al andere protocollen).
export const veiligeLink = (url) => (/^https?:\/\//i.test(String(url || '')) ? url : null);

export function downloadBlob(blob, bestandsnaam) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = bestandsnaam;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function naamUitHeader(header, standaard) {
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header || '');
  if (utf) { try { return decodeURIComponent(utf[1]); } catch { /* val terug */ } }
  const gewoon = /filename="([^"]+)"/i.exec(header || '');
  return gewoon ? gewoon[1] : standaard;
}

export async function downloadExport(pad, standaard, wat) {
  const r = await authFetch(`${BASIS}${pad}`);
  if (!r.ok) await leesJson(r, wat);
  downloadBlob(await r.blob(), naamUitHeader(r.headers.get('Content-Disposition'), standaard));
}

const P = (id) => `/productions/${encodeURIComponent(id)}`;
const I = (id) => `/items/${encodeURIComponent(id)}`;

// ── Producties ───────────────────────────────────────────
export const zoekProjecten = (q) => api(`/projects?q=${encodeURIComponent(q)}`, { wat: 'Projecten zoeken' });
export const lijstProducties = () => api('/productions', { wat: 'Producties laden' });
export const overzicht = () => api('/dashboard', { wat: 'Overzicht laden' });
export const haalProductie = (id) => api(P(id), { wat: 'Productie laden' });
export const nieuweProductie = (body) => api('/productions', { method: 'POST', body, wat: 'Productie aanmaken' });
export const wijzigProductie = (id, body) => api(P(id), { method: 'PATCH', body, wat: 'Productie opslaan' });

// ── Items, opties, documenten ────────────────────────────
export const lijstItems = (pid) => api(`${P(pid)}/items`, { wat: 'Items laden' });
export const nieuwItem = (pid, body) => api(`${P(pid)}/items`, { method: 'POST', body, wat: 'Item toevoegen' });
export const wijzigItem = (id, body) => perItem(id, () => api(I(id), { method: 'PATCH', body, wat: 'Item opslaan' }));
export const dupliceerItem = (id) => perItem(id, () => api(`${I(id)}/duplicate`, { method: 'POST', wat: 'Item dupliceren' }));
export const nieuweOptie = (id, body) => perItem(id, () => api(`${I(id)}/options`, { method: 'POST', body, wat: 'Optie toevoegen' }));
export const kiesOptie = (id, optieId) => perItem(id, () => api(`${I(id)}/choose-option`, { method: 'POST', body: { option_id: optieId }, wat: 'Optie kiezen' }));
export function fotoToevoegen(id, optieId, bestand) {
  const form = new FormData();
  form.append('file', bestand);
  return perItem(id, () => api(`${I(id)}/options/${optieId}/photos`, { method: 'POST', form, wat: 'Foto uploaden' }));
}
export const lijstDocumenten = (id) => api(`${I(id)}/documents`, { wat: 'Documenten laden' });
export const nieuwDocument = (id, name) => api(`${I(id)}/documents`, { method: 'POST', body: { name }, wat: 'Document toevoegen' });
export const wijzigDocument = (id, docId, body) => api(`${I(id)}/documents/${docId}`, { method: 'PATCH', body, wat: 'Document opslaan' });
export function bijlageToevoegen(id, docId, bestand) {
  const form = new FormData();
  form.append('file', bestand);
  return api(`${I(id)}/documents/${docId}/file`, { method: 'POST', form, wat: 'Bijlage uploaden' });
}
export const bijlageLink = (id, docId) => api(`${I(id)}/documents/${docId}/file`, { wat: 'Bijlage openen' });
export const documentSuggesties = (categorie) => api(`/doc-templates?category=${encodeURIComponent(categorie)}`, { wat: 'Suggesties laden' });
export const documentTemplate = (category, name) => api('/doc-templates', { method: 'POST', body: { category, name }, wat: 'Template bewaren' });

// ── Scenes, shots, analyse ───────────────────────────────
export const lijstScenes = (pid) => api(`${P(pid)}/scenes`, { wat: 'Scenes laden' });
export const nieuweScene = (pid, body) => api(`${P(pid)}/scenes`, { method: 'POST', body, wat: 'Scene toevoegen' });
export const wijzigScene = (id, body) => api(`/scenes/${id}`, { method: 'PATCH', body, wat: 'Scene opslaan' });
export const lijstShots = (pid) => api(`${P(pid)}/shots`, { wat: 'Shots laden' });
export const nieuwShot = (pid, body) => api(`${P(pid)}/shots`, { method: 'POST', body, wat: 'Shot toevoegen' });
export const wijzigShot = (id, body) => api(`/shots/${id}`, { method: 'PATCH', body, wat: 'Shot opslaan' });
export const analyseer = (pid, text) => api(`${P(pid)}/analyze`, { method: 'POST', body: { text }, wat: 'Analyse' });

// ── Materiaal en crew ────────────────────────────────────
export const lijstMateriaal = (pid) => api(`${P(pid)}/equipment`, { wat: 'Materiaal laden' });
export const nieuwMateriaal = (pid, body) => api(`${P(pid)}/equipment`, { method: 'POST', body, wat: 'Materiaal toevoegen' });
export const wijzigMateriaal = (id, body) => api(`/equipment/${id}`, { method: 'PATCH', body, wat: 'Materiaal opslaan' });
export const lijstCrew = (pid) => api(`${P(pid)}/crew`, { wat: 'Crew laden' });
export const nieuweCrew = (pid, body) => api(`${P(pid)}/crew`, { method: 'POST', body, wat: 'Crew toevoegen' });
export const wijzigCrew = (id, body) => api(`/crew/${id}`, { method: 'PATCH', body, wat: 'Crew opslaan' });
export const crewTemplates = () => api('/crew-templates', { wat: 'Templates laden' });
export const crewTemplateOpslaan = (name, members) => api('/crew-templates', { method: 'POST', body: { name, members }, wat: 'Template bewaren' });
export const crewTemplateToepassen = (pid, tid) => api(`${P(pid)}/crew-templates/${tid}/apply`, { method: 'POST', wat: 'Template toepassen' });

// ── Planning en stripboard ───────────────────────────────
export const haalPlanning = (pid) => api(`${P(pid)}/planning`, { wat: 'Planning laden' });
export const wijzigPlanning = (pid, body) => api(`${P(pid)}/planning`, { method: 'PATCH', body, wat: 'Planning opslaan' });
export const lijstSchemas = (pid) => api(`${P(pid)}/boards`, { wat: 'Schema\'s laden' });
export const nieuwSchema = (pid, name, duplicateFrom) => api(`${P(pid)}/boards`, { method: 'POST', body: { name, duplicate_from: duplicateFrom || null }, wat: 'Schema aanmaken' });
export const toewijzingen = (bid) => api(`/boards/${bid}/assignments`, { wat: 'Stripboard laden' });
export const zetToewijzing = (bid, sceneId, body) => api(`/boards/${bid}/scenes/${sceneId}`, { method: 'PATCH', body, wat: 'Inplannen' });
export const wissel = (a, b) => api('/boards/swap-order', { method: 'POST', body: { assignment_a_id: a, assignment_b_id: b }, wat: 'Volgorde wijzigen' });
export const conflicten = (pid, bid) => api(`${P(pid)}/boards/${bid}/conflicts`, { wat: 'Conflicten laden' });

// ── Log, budget, exports ─────────────────────────────────
export const lijstLog = (pid) => api(`${P(pid)}/activity`, { wat: 'Log laden' });
export const haalBudget = (pid) => api(`${P(pid)}/budget`, { wat: 'Budget laden' });
export const exportJson = (pid, soort) => api(`${P(pid)}/export/${soort}.json`, { wat: 'Export' });
export function cadreImport(pid, bestand) {
  const form = new FormData();
  form.append('file', bestand);
  return api(`${P(pid)}/import/cadre`, { method: 'POST', form, wat: 'Cadre-import' });
}
export const bestandExport = (pid, pad, standaard) => downloadExport(`${P(pid)}/export/${pad}`, standaard, 'Export');

// Moet gelijk blijven aan CATEGORIEEN / STATUSSEN / SHOT_TYPES in ai-engine/models/productie_breakdown.py.
export const CATEGORIEEN = [
  { key: 'cast', label: 'Cast', icoon: '🎭' },
  { key: 'locatie', label: 'Locaties', icoon: '📍' },
  { key: 'prop', label: 'Props', icoon: '📦' },
  { key: 'voertuig', label: 'Voertuigen', icoon: '🚗' },
  { key: 'wardrobe', label: 'Wardrobe', icoon: '👕' },
  { key: 'sfx', label: 'SFX / Stunts', icoon: '💥' },
  { key: 'overig', label: 'Overig', icoon: '📋' },
];
export const CATEGORIE = Object.fromEntries(CATEGORIEEN.map((c) => [c.key, c]));

export const STATUSSEN = {
  open: { label: 'Nog te regelen', kleur: '#5f6b7a', bg: '#f1f3f6' },
  toegezegd: { label: 'Toegezegd', kleur: '#b45309', bg: '#fef3c7' },
  bevestigd: { label: 'Bevestigd', kleur: '#15803d', bg: '#dcfce7' },
};
export const STATUS_VOLGORDE = ['open', 'toegezegd', 'bevestigd'];
export const SHOT_TYPES = ['Close-up', 'Medium shot', 'Wide / Totaal', 'Detail / Insert', 'Beweging / Tracking', 'Drone', 'Anders'];

export const INKT = '#1f2937';
// Contrast ≥ 4.5:1 op wit, de kolom en de open-status (#8a94a3 haalde maar ~3:1).
export const STIL = '#5f6b7a';
export const LIJN = '#eceef1';
export const ACCENT = '#526075';
export const KOLOM_BG = '#fafbfc';

export function vandaagIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function korteDatum(iso) {
  if (!iso) return '';
  const [j, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(j, m - 1, d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}
